/**
 * Импорт видео с публичной ссылки Яндекс.Диска.
 *
 * Браузер не качает файл и не грузит его на сервер — это делает backend
 * (`POST /api/files/admin/import-yandex`). Фронт только отправляет URL и
 * ждёт запуска фоновой задачи; прогресс HLS-обработки виден стандартным
 * polling'ом `/admin/video/:id/status` (делает VideoUpload сам).
 */

import { useCallback, useState } from 'react';
import { useApiUrl } from '@refinedev/core';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, DownloadCloud, AlertCircle, CheckCircle2 } from 'lucide-react';

interface YandexDiskImportProps {
  moduleItemId?: string;
  disabled?: boolean;
  /** Вызывается сразу после того, как бэкенд подтвердил постановку задачи в очередь */
  onImportStarted?: () => void;
}

interface ImportApiResponse {
  success: boolean;
  data: {
    jobId: string;
    moduleItemId: number;
    fileName: string;
    fileSize: number;
    mimeType: string;
    status: string;
  } | null;
  errors: string[] | null;
}

function isImportApiResponse(value: unknown): value is ImportApiResponse {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as { success?: unknown };
  return typeof candidate.success === 'boolean';
}

function formatMb(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) {
    return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} ГБ`;
  }
  return `${(bytes / 1024 / 1024).toFixed(0)} МБ`;
}

export function YandexDiskImport({
  moduleItemId,
  disabled,
  onImportStarted,
}: YandexDiskImportProps) {
  const apiUrl = useApiUrl();
  const [url, setUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ fileName: string; fileSize: number } | null>(null);

  const handleSubmit = useCallback(async () => {
    setError(null);
    setSuccess(null);

    if (!moduleItemId) {
      setError('Сначала сохраните урок — импорт доступен только для существующего элемента');
      return;
    }
    const trimmed = url.trim();
    if (trimmed.length === 0) {
      setError('Введите ссылку с Яндекс.Диска');
      return;
    }

    setIsSubmitting(true);
    try {
      // Глобальный fetch interceptor сам добавляет Authorization и рефрешит токен
      // при 401 — дополнительные заголовки не нужны.
      const response = await fetch(`${apiUrl}/files/admin/import-yandex`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          moduleItemId: parseInt(moduleItemId, 10),
          url: trimmed,
        }),
      });

      const raw: unknown = await response.json().catch(() => ({}));
      if (!isImportApiResponse(raw)) {
        throw new Error('Некорректный ответ сервера');
      }
      if (!response.ok || !raw.success || raw.data === null) {
        const message =
          raw.errors !== null && raw.errors.length > 0
            ? raw.errors.join(', ')
            : `Ошибка импорта (HTTP ${response.status})`;
        throw new Error(message);
      }

      setSuccess({ fileName: raw.data.fileName, fileSize: raw.data.fileSize });
      setUrl('');
      onImportStarted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка импорта');
    } finally {
      setIsSubmitting(false);
    }
  }, [apiUrl, moduleItemId, onImportStarted, url]);

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <label
          htmlFor="yandex-disk-url"
          className="text-sm font-medium text-muted-foreground"
        >
          Публичная ссылка с Яндекс.Диска
        </label>
        <div className="flex gap-2">
          <Input
            id="yandex-disk-url"
            type="url"
            placeholder="https://disk.yandex.ru/d/..."
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            disabled={disabled || isSubmitting}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSubmit();
              }
            }}
          />
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={disabled || isSubmitting || url.trim().length === 0}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                Отправка...
              </>
            ) : (
              <>
                <DownloadCloud className="h-4 w-4 mr-1.5" />
                Импортировать
              </>
            )}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground/70">
          Поддерживаются только ссылки на отдельные видеофайлы. Скачивание с Яндекс.Диска
          выполняется на сервере, дальше запускается HLS-обработка как обычно.
        </p>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-md border border-destructive/50 bg-destructive/5 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="flex items-start gap-2 p-3 rounded-md border border-green-500/50 bg-green-500/5 text-sm text-green-700 dark:text-green-400">
          <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            Импорт запущен: <strong>{success.fileName}</strong> ({formatMb(success.fileSize)}).
            После скачивания автоматически начнётся HLS-обработка.
          </span>
        </div>
      )}
    </div>
  );
}
